import React, { useState, useEffect } from 'react';
import axios from './api';
import Auth from './Auth';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [children, setChildren] = useState([]);
  const [upcomingCalls, setUpcomingCalls] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [themes, setThemes] = useState(null);
  const [activeTab, setActiveTab] = useState('children');

  // Form states
  const [childForm, setChildForm] = useState({
    name: '',
    phone_number: '',
    parent_name: '',
    parent_phone: ''
  });

  const [callForm, setCallForm] = useState({
    child_id: '',
    theme: 'NICE',
    sub_theme: '',
    caller_type: 'SANTA',
    scheduled_time: ''
  });

  const [immediateCallForm, setImmediateCallForm] = useState({
    child_id: '',
    theme: 'NICE',
    sub_theme: '',
    caller_type: 'SANTA',
    madLibsData: null
  });

  const [madLibsInputs, setMadLibsInputs] = useState({
    achievement: '',
    specificDetail: '',
    location: '',
    rewardOrPraise: ''
  });

  const [isImmediateCallModalOpen, setIsImmediateCallModalOpen] = useState(false);
  const [callLinkModal, setCallLinkModal] = useState({ isOpen: false, link: '', childName: '' });

  // Real phone calls are off in v1; phone fields only show when the server enables them
  const [phoneCallsEnabled, setPhoneCallsEnabled] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
    axios.get('/api/config')
      .then((response) => setPhoneCallsEnabled(Boolean(response.data.phoneCallsEnabled)))
      .catch((error) => console.error('Error loading config:', error));
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadChildren();
      loadThemes();
      loadUpcomingCalls();
      loadCallLogs();
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setUser(response.data.user);
      }
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
      setIsAuthenticated(false);
      setUser(null);
      setChildren([]);
      setUpcomingCalls([]);
      setCallLogs([]);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const loadChildren = async () => {
    try {
      const response = await axios.get('/api/children');
      setChildren(response.data);
    } catch (error) {
      console.error('Error loading children:', error);
    }
  };

  const loadThemes = async () => {
    try {
      const response = await axios.get('/api/themes');
      setThemes(response.data);
    } catch (error) {
      console.error('Error loading themes:', error);
    }
  };

  const loadUpcomingCalls = async () => {
    try {
      const response = await axios.get('/api/calls/upcoming');
      setUpcomingCalls(response.data);
    } catch (error) {
      console.error('Error loading upcoming calls:', error);
    }
  };

  const loadCallLogs = async () => {
    try {
      const response = await axios.get('/api/logs');
      setCallLogs(response.data);
    } catch (error) {
      console.error('Error loading call logs:', error);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/children', childForm);
      setChildForm({ name: '', phone_number: '', parent_name: '', parent_phone: '' });
      loadChildren();
      alert('Child added successfully!');
    } catch (error) {
      alert('Error adding child: ' + error.message);
    }
  };

  const handleDeleteChild = async (id) => {
    if (window.confirm('Are you sure you want to delete this child?')) {
      try {
        await axios.delete(`/api/children/${id}`);
        loadChildren();
      } catch (error) {
        alert('Error deleting child: ' + error.message);
      }
    }
  };

  const handleScheduleCall = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/calls/schedule', callForm);
      setCallForm({
        child_id: '',
        theme: 'NICE',
        sub_theme: '',
        caller_type: 'SANTA',
        scheduled_time: ''
      });
      loadUpcomingCalls();
      alert('Call scheduled successfully!');
    } catch (error) {
      alert('Error scheduling call: ' + error.message);
    }
  };

  const openImmediateCallModal = (childId) => {
    setImmediateCallForm({ ...immediateCallForm, child_id: childId });
    setIsImmediateCallModalOpen(true);
  };

  const closeImmediateCallModal = () => {
    setIsImmediateCallModalOpen(false);
    setImmediateCallForm({
      child_id: '',
      theme: 'NICE',
      sub_theme: '',
      caller_type: 'SANTA',
      madLibsData: null
    });
    setMadLibsInputs({
      achievement: '',
      specificDetail: '',
      location: '',
      rewardOrPraise: ''
    });
  };

  const handleImmediateCallSubmit = async (e) => {
    e.preventDefault();
    try {
      // Include mad libs data if in MAD_LIBS mode
      const formData = { ...immediateCallForm };
      if (immediateCallForm.theme === 'MAD_LIBS') {
        formData.madLibsData = madLibsInputs;
      }

      const response = await axios.post('/api/calls/web', formData);
      console.log('Call created:', response.data);

      // Show the call link modal
      setCallLinkModal({
        isOpen: true,
        link: response.data.callLink,
        childName: response.data.childName
      });

      closeImmediateCallModal();
      loadCallLogs();
    } catch (error) {
      alert('Error creating call: ' + error.message);
    }
  };

  const handleImmediateCall = async (childId) => {
    openImmediateCallModal(childId);
  };

  const getSubThemes = (theme) => {
    if (!themes || !theme) return [];
    return Object.entries(themes[theme].subThemes).map(([key, value]) => ({
      key,
      name: value.name
    }));
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="App" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh'}}>
        <div style={{textAlign: 'center'}}>
          <h2>🎅 Loading...</h2>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Auth onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div>
            <h1>🎅 North Pole Line 🎄</h1>
            <p>Spread Christmas cheer one call at a time!</p>
          </div>
          <div className="header-user">
            <span className="user-name">👤 {user?.name}</span>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>

      {isImmediateCallModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Create Call</h2>
            <form onSubmit={handleImmediateCallSubmit} className="form">
              <select
                value={immediateCallForm.theme}
                onChange={(e) => {
                  const newTheme = e.target.value;
                  setImmediateCallForm({
                    ...immediateCallForm,
                    theme: newTheme,
                    sub_theme: newTheme === 'MAD_LIBS' ? 'CUSTOM' : ''
                  });
                  // Reset mad libs inputs when switching themes
                  if (newTheme !== 'MAD_LIBS') {
                    setMadLibsInputs({
                      achievement: '',
                      specificDetail: '',
                      location: '',
                      rewardOrPraise: ''
                    });
                  }
                }}
                required
              >
                <option value="NICE">✨ Nice List</option>
                <option value="NAUGHTY">⚠️ Needs Improvement</option>
                <option value="MAD_LIBS">🎨 Custom Message (Mad Libs)</option>
              </select>

              {immediateCallForm.theme !== 'MAD_LIBS' ? (
                <select
                  value={immediateCallForm.sub_theme}
                  onChange={(e) => setImmediateCallForm({...immediateCallForm, sub_theme: e.target.value})}
                  required
                >
                  <option value="">Select Reason</option>
                  {getSubThemes(immediateCallForm.theme).map(theme => (
                    <option key={theme.key} value={theme.key}>{theme.name}</option>
                  ))}
                </select>
              ) : (
                <div className="mad-libs-inputs">
                  <div className="input-group">
                    <label htmlFor="achievement">What did they do well? *</label>
                    <input
                      id="achievement"
                      type="text"
                      placeholder="e.g., 'cleaned your room without being asked'"
                      value={madLibsInputs.achievement}
                      onChange={(e) => setMadLibsInputs({...madLibsInputs, achievement: e.target.value})}
                      required
                    />
                    <small>Example: "you helped your sister with her homework"</small>
                  </div>

                  <div className="input-group">
                    <label htmlFor="specificDetail">Add a specific detail</label>
                    <input
                      id="specificDetail"
                      type="text"
                      placeholder="e.g., 'You even organized your toys!'"
                      value={madLibsInputs.specificDetail}
                      onChange={(e) => setMadLibsInputs({...madLibsInputs, specificDetail: e.target.value})}
                    />
                    <small>Optional: Add more context about what they did</small>
                  </div>

                  <div className="input-group">
                    <label htmlFor="location">Where or when?</label>
                    <input
                      id="location"
                      type="text"
                      placeholder="e.g., 'at school' or 'yesterday'"
                      value={madLibsInputs.location}
                      onChange={(e) => setMadLibsInputs({...madLibsInputs, location: e.target.value})}
                    />
                    <small>Optional: Where or when did this happen?</small>
                  </div>

                  <div className="input-group">
                    <label htmlFor="rewardOrPraise">Special message or reward</label>
                    <input
                      id="rewardOrPraise"
                      type="text"
                      placeholder="e.g., 'I'm adding extra presents for you!'"
                      value={madLibsInputs.rewardOrPraise}
                      onChange={(e) => setMadLibsInputs({...madLibsInputs, rewardOrPraise: e.target.value})}
                    />
                    <small>Optional: Custom praise or reward message</small>
                  </div>
                </div>
              )}

              <select
                value={immediateCallForm.caller_type}
                onChange={(e) => setImmediateCallForm({...immediateCallForm, caller_type: e.target.value})}
                required
              >
                <option value="SANTA">🎅 Santa Claus</option>
                <option value="ELF_MALE">🧝‍♂️ Jingle Jack (Male Elf)</option>
                <option value="ELF_FEMALE">🧝‍♀️ Snowdrop Ellie (Female Elf)</option>
                <option value="MRS_CLAUS">👵 Mrs. Claus</option>
                <option value="GRANDPA_JOE">🎅 Santa Option 2</option>
              </select>

              <div className="modal-actions">
                <button type="submit">Generate Call Link</button>
                <button type="button" onClick={closeImmediateCallModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {callLinkModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal call-link-modal">
            <h2>🎅 Call Link Ready!</h2>
            <p className="modal-subtitle">
              Share this link with {callLinkModal.childName} at the perfect moment!
            </p>

            <div className="call-link-container">
              <input
                type="text"
                value={callLinkModal.link}
                readOnly
                className="call-link-input"
                onClick={(e) => e.target.select()}
              />
              <button
                className="copy-btn"
                onClick={() => {
                  // Use clipboard API if available, otherwise fallback for HTTP
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(callLinkModal.link);
                    alert('Link copied to clipboard!');
                  } else {
                    // Fallback for HTTP - use textarea and execCommand
                    const textarea = document.createElement('textarea');
                    textarea.value = callLinkModal.link;
                    textarea.style.position = 'fixed';
                    textarea.style.opacity = '0';
                    document.body.appendChild(textarea);
                    textarea.select();
                    try {
                      document.execCommand('copy');
                      alert('Link copied to clipboard!');
                    } catch (err) {
                      alert('Copy failed. Please select and copy manually.');
                    }
                    document.body.removeChild(textarea);
                  }
                }}
              >
                📋 Copy Link
              </button>
            </div>

            <div className="modal-tips">
              <h4>💡 Tips:</h4>
              <ul>
                <li>Send via text, email, or open directly</li>
                <li>Works on any device with a browser</li>
                <li>Child will see an incoming call screen</li>
                <li>Link expires in 7 days</li>
              </ul>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  window.open(callLinkModal.link, '_blank');
                }}
                className="btn-test"
              >
                🧪 Test Call
              </button>
              <button
                onClick={() => setCallLinkModal({ isOpen: false, link: '', childName: '' })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <nav className="tabs">
        <button 
          className={activeTab === 'children' ? 'active' : ''}
          onClick={() => setActiveTab('children')}
        >
          👶 Children
        </button>
        <button 
          className={activeTab === 'schedule' ? 'active' : ''}
          onClick={() => setActiveTab('schedule')}
        >
          📅 Schedule Calls
        </button>
        <button 
          className={activeTab === 'upcoming' ? 'active' : ''}
          onClick={() => setActiveTab('upcoming')}
        >
          ⏰ Upcoming Calls
        </button>
        <button 
          className={activeTab === 'logs' ? 'active' : ''}
          onClick={() => setActiveTab('logs')}
        >
          📞 Call History
        </button>
      </nav>

      <main className="content">
        {/* CHILDREN TAB */}
        {activeTab === 'children' && (
          <div className="section">
            <h2>Manage Children</h2>
            
            <form onSubmit={handleAddChild} className="form">
              <h3>Add New Child</h3>
              <input
                type="text"
                placeholder="Child's Name"
                value={childForm.name}
                onChange={(e) => setChildForm({...childForm, name: e.target.value})}
                required
              />
              {phoneCallsEnabled && (
                <>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={childForm.phone_number}
                    onChange={(e) => setChildForm({...childForm, phone_number: e.target.value})}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Parent's Name"
                    value={childForm.parent_name}
                    onChange={(e) => setChildForm({...childForm, parent_name: e.target.value})}
                  />
                  <input
                    type="tel"
                    placeholder="Parent's Phone"
                    value={childForm.parent_phone}
                    onChange={(e) => setChildForm({...childForm, parent_phone: e.target.value})}
                  />
                </>
              )}
              <button type="submit">Add Child</button>
            </form>

            <div className="list">
              <h3>Children List</h3>
              {children.length === 0 ? (
                <p className="empty">No children added yet. Add your first child above!</p>
              ) : (
                <div className="cards">
                  {children.map(child => (
                    <div key={child.id} className="card">
                      <h4>{child.name}</h4>
                      {phoneCallsEnabled && child.phone_number && <p>📞 {child.phone_number}</p>}
                      {child.parent_name && <p>👤 {child.parent_name}</p>}
                      <div className="card-actions">
                        <button
                          onClick={() => handleImmediateCall(child.id)}
                          className="btn-call"
                        >
                          🎅 Create Call Link
                        </button>
                        <button 
                          onClick={() => handleDeleteChild(child.id)}
                          className="btn-delete"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SCHEDULE CALLS TAB */}
        {activeTab === 'schedule' && (
          <div className="section">
            <h2>Schedule a Call</h2>
            
            <form onSubmit={handleScheduleCall} className="form">
              <select
                value={callForm.child_id}
                onChange={(e) => setCallForm({...callForm, child_id: e.target.value})}
                required
              >
                <option value="">Select Child</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>{child.name}</option>
                ))}
              </select>

              <select
                value={callForm.theme}
                onChange={(e) => setCallForm({...callForm, theme: e.target.value, sub_theme: ''})}
                required
              >
                <option value="NICE">✨ Nice List</option>
                <option value="NAUGHTY">⚠️ Needs Improvement</option>
              </select>

              <select
                value={callForm.sub_theme}
                onChange={(e) => setCallForm({...callForm, sub_theme: e.target.value})}
                required
              >
                <option value="">Select Reason</option>
                {getSubThemes(callForm.theme).map(theme => (
                  <option key={theme.key} value={theme.key}>{theme.name}</option>
                ))}
              </select>

              <select
                value={callForm.caller_type}
                onChange={(e) => setCallForm({...callForm, caller_type: e.target.value})}
                required
              >
                <option value="SANTA">🎅 Santa Claus</option>
                <option value="ELF_MALE">🧝‍♂️ Jingle Jack (Male Elf)</option>
                <option value="ELF_FEMALE">🧝‍♀️ Snowdrop Ellie (Female Elf)</option>
                <option value="MRS_CLAUS">👵 Mrs. Claus</option>
                <option value="GRANDPA_JOE">🎅 Santa Option 2</option>
              </select>

              <input
                type="datetime-local"
                value={callForm.scheduled_time}
                onChange={(e) => setCallForm({...callForm, scheduled_time: e.target.value})}
                required
              />

              <button type="submit">Schedule Call</button>
            </form>
          </div>
        )}

        {/* UPCOMING CALLS TAB */}
        {activeTab === 'upcoming' && (
          <div className="section">
            <h2>Upcoming Scheduled Calls</h2>
            {upcomingCalls.length === 0 ? (
              <p className="empty">No upcoming calls scheduled.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Child</th>
                      <th>Theme</th>
                      <th>Caller</th>
                      <th>Scheduled Time</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingCalls.map(call => (
                      <tr key={call.id}>
                        <td>{call.name}</td>
                        <td>{call.theme} - {call.sub_theme}</td>
                        <td>{call.caller_type}</td>
                        <td>{new Date(call.scheduled_time).toLocaleString()}</td>
                        <td><span className={`status ${call.status}`}>{call.status}</span></td>
                        <td>
                          <button 
                            onClick={async () => {
                              if (window.confirm('Delete this scheduled call?')) {
                                await axios.delete(`/api/calls/${call.id}`);
                                loadUpcomingCalls();
                              }
                            }}
                            className="btn-delete-small"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* CALL LOGS TAB */}
        {activeTab === 'logs' && (
          <div className="section">
            <h2>Call History</h2>
            {callLogs.length === 0 ? (
              <p className="empty">No calls made yet.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Child</th>
                      <th>Theme</th>
                      <th>Caller</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callLogs.map(log => {
                      const isExpired = log.link_expiration && new Date(log.link_expiration) < new Date();
                      const hasLink = log.web_call_link && !isExpired;

                      return (
                        <tr key={log.id}>
                          <td>{new Date(log.created_at).toLocaleString()}</td>
                          <td>{log.child_name}</td>
                          <td>{log.theme} - {log.sub_theme}</td>
                          <td>{log.caller_type}</td>
                          <td>{log.duration ? `${log.duration}s` : '-'}</td>
                          <td><span className={`status ${log.status}`}>{log.status || 'completed'}</span></td>
                          <td>
                            {hasLink ? (
                              <button
                                className="btn-call-small"
                                onClick={() => window.open(log.web_call_link, '_blank')}
                                title="Replay this call"
                              >
                                🔄 Replay
                              </button>
                            ) : (
                              isExpired ? (
                                <span className="expired-text" style={{color: '#999', fontSize: '0.85em'}}>Expired</span>
                              ) : (
                                <span style={{color: '#999'}}>-</span>
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="footer">
        <p>Made with ❤️ and Christmas magic 🎄</p>
      </footer>
    </div>
  );
}

export default App;
